import { User } from "@prisma/client";
import { create } from "zustand";
import { devtools, persist, createJSONStorage } from "zustand/middleware";
import { readAuthPolicy } from "@app/utils/auth-policy";

/** What the login page says of a session that ended by itself (PT-171). */
interface LapsedSession {
  minutes: number | null;
}

interface State {
  user: User | null;
  lapsedSession: LapsedSession | null;
}

interface Action {
  setUser: (user: User | null) => void;
  /** The API refused the session. Signing out goes through `setUser(null)`, and says nothing. */
  endSession: () => void;
  dismissLapsedSession: () => void;
}

const useUser = create<State & Action>()(
  devtools(
    persist(
      (set): State & Action => ({
        user: null,
        lapsedSession: null,
        setUser: (user: User | null) => set({ user }),
        endSession: () =>
          set(({ user, lapsedSession }) => {
            if (!user) return { user, lapsedSession };
            // An enrollment session has no idle window: it ends with its wizard.
            if (readAuthPolicy(user)?.state === "required") {
              return { user: null, lapsedSession };
            }
            const { session_idle_minutes: minutes } = user as {
              session_idle_minutes?: number;
            };
            return { user: null, lapsedSession: { minutes: minutes ?? null } };
          }),
        dismissLapsedSession: () => set({ lapsedSession: null }),
      }),
      {
        name: "mage-x-zustand-user-store",
        version: 1,
        storage: createJSONStorage(() => window.localStorage),
      },
    ),
  ),
);

export default useUser;
